import { useEffect, useState } from "react";
import { Ban, Plus, X, Loader2, Save, UserCheck, Mail, Zap, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function StopWordsCard() {
  const [standard, setStandard] = useState([]);
  const [extra, setExtra] = useState([]);
  const [draft, setDraft] = useState("");
  const [resubEnabled, setResubEnabled] = useState(true);
  const [resubMsg, setResubMsg] = useState("");
  const [alertThreshold, setAlertThreshold] = useState(2);
  const [alertMinSent, setAlertMinSent] = useState(20);
  const [pauseTplId, setPauseTplId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [creatingPreset, setCreatingPreset] = useState(false);
  const [creatingTpl, setCreatingTpl] = useState(false);
  const [tplResult, setTplResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const createWelcomeBack = async () => {
    setCreatingTpl(true);
    try {
      const { data } = await api.post("/templates/presets/welcome-back");
      setTplResult(data);
      setPauseTplId(data.template?.id || "");
      if (data.template && !templates.some((t) => t.id === data.template.id)) setTemplates([...templates, data.template]);
      toast[data.meta_error ? "warning" : "success"](data.meta_error ? "Template salvato, invio a Meta non riuscito" : "Template «Bentornato» inviato a Meta per approvazione");
    } catch (err) { toast.error(err.response?.data?.detail || "Creazione non riuscita"); }
    finally { setCreatingTpl(false); }
  };

  useEffect(() => {
    Promise.all([api.get("/settings/stop-words"), api.get("/settings"), api.get("/templates").catch(() => ({ data: [] }))]).then(([sw, s, t]) => {
      setStandard(sw.data.standard || []);
      setExtra(sw.data.extra || []);
      setResubEnabled(s.data.resubscribe_enabled !== false);
      setResubMsg(s.data.resubscribe_message || "");
      setAlertThreshold(s.data.stop_alert_threshold ?? 2);
      setAlertMinSent(s.data.stop_alert_min_sent ?? 20);
      setPauseTplId(s.data.pause_end_template_id || "");
      const tl = Array.isArray(t.data) ? t.data : t.data?.items || [];
      setTemplates(tl.filter((x) => x.meta_name));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const createPreset = async () => {
    setCreatingPreset(true);
    try {
      const { data } = await api.post("/automations/presets/unsubscribe");
      toast.success(data.created ? "Automazione «Disiscrizione con motivo» creata: aprila in Automazioni per personalizzarla" : "Esiste già: aprila in Automazioni");
      window.location.href = `/automazioni?open=${data.id}`;
    } catch { toast.error("Creazione non riuscita"); }
    finally { setCreatingPreset(false); }
  };

  const add = () => {
    const w = draft.trim().toLowerCase();
    if (!w) return;
    if (extra.includes(w) || standard.map((x) => x.toLowerCase()).includes(w)) { toast.info("Parola già presente"); setDraft(""); return; }
    setExtra([...extra, w]);
    setDraft("");
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { stop_words_extra: extra, resubscribe_enabled: resubEnabled, resubscribe_message: resubMsg,
                                                   stop_alert_threshold: Number(alertThreshold), stop_alert_min_sent: Number(alertMinSent), pause_end_template_id: pauseTplId === "none" ? "" : pauseTplId });
      setExtra(data.stop_words_extra || []);
      toast.success("Impostazioni disiscrizione salvate");
    } catch { toast.error("Errore durante il salvataggio"); }
    finally { setSaving(false); }
  };

  if (!loaded) return null;
  return (
    <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 space-y-4" data-testid="stop-words-card">
      <div className="flex items-center gap-2">
        <Ban className="h-5 w-5 text-[#EF4444]" />
        <h3 className="font-display text-lg font-semibold text-slate-200">Parole di disiscrizione e re-iscrizione</h3>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">Parole standard (sempre attive)</p>
        <div className="flex flex-wrap gap-1.5" data-testid="stop-words-standard">
          {standard.map((w) => <span key={w} className="rounded-full bg-[#1E293B] text-slate-400 px-2.5 py-0.5 text-xs font-mono">{w}</span>)}
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">Parole o frasi extra (es. «basta», «non scrivermi»)</p>
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="stop-words-extra">
          {extra.map((w) => (
            <span key={w} data-testid={`stop-word-${w}`} className="inline-flex items-center gap-1 rounded-full bg-[#EF4444]/15 text-[#EF4444] px-2.5 py-0.5 text-xs font-mono">
              {w}
              <button type="button" onClick={() => setExtra(extra.filter((x) => x !== w))} data-testid={`stop-word-remove-${w}`} className="hover:text-slate-100"><X className="h-3 w-3" /></button>
            </span>
          ))}
          {!extra.length && <span className="text-xs text-slate-500">Nessuna parola extra.</span>}
        </div>
        <div className="flex gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                 placeholder="Aggiungi parola o frase e premi Invio" data-testid="stop-word-input" className="bg-[#0A0E17] border-[#1F2937] text-slate-100 h-9" />
          <Button type="button" variant="outline" onClick={add} data-testid="stop-word-add" className="bg-[#1E293B] border-[#1F2937] text-slate-200 h-9 shrink-0"><Plus className="h-4 w-4" /></Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">Il messaggio del cliente deve corrispondere all'intera parola/frase (maiuscole e punteggiatura ignorate). Lo STOP disiscrive subito e, se esiste, avvia l'automazione con quel trigger.</p>
      </div>
      <div className="bg-[#0A0E17] rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-[#10B981]" />
            <span className="text-sm text-slate-300">Conferma automatica di re-iscrizione (quando un disiscritto scrive «Sì», «Iscrivimi», «Start»…)</span>
          </div>
          <Switch checked={resubEnabled} onCheckedChange={setResubEnabled} data-testid="resubscribe-enabled-switch" />
        </div>
        <Textarea value={resubMsg} onChange={(e) => setResubMsg(e.target.value)} rows={2} disabled={!resubEnabled} data-testid="resubscribe-message-input"
                  placeholder="Messaggio di conferma, puoi usare {{nome}}" className="bg-[#111827] border-[#1F2937] text-slate-100 text-sm resize-none" />
        <p className="text-[11px] text-slate-500">Se esiste un'automazione con trigger «Adesione marketing» o con la parola scritta dal cliente, parte quella al posto di questo messaggio.</p>
      </div>
      <div className="bg-[#0A0E17] rounded-lg p-3 space-y-2" data-testid="stop-alert-settings">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-[#F59E0B]" />
          <span className="text-sm text-slate-300">Avviso email all'admin quando una campagna supera la soglia di STOP</span>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
          <label className="flex items-center gap-1.5">Soglia
            <Input type="number" min="0" max="100" step="0.5" value={alertThreshold} onChange={(e) => setAlertThreshold(e.target.value)} data-testid="stop-alert-threshold-input"
                   className="h-8 w-20 bg-[#111827] border-[#1F2937] text-slate-100 text-sm" /> % di STOP
          </label>
          <label className="flex items-center gap-1.5">su almeno
            <Input type="number" min="1" value={alertMinSent} onChange={(e) => setAlertMinSent(e.target.value)} data-testid="stop-alert-min-sent-input"
                   className="h-8 w-24 bg-[#111827] border-[#1F2937] text-slate-100 text-sm" /> invii
          </label>
          <span>· 0 = disattivato · una sola email per campagna</span>
        </div>
      </div>
      <div className="bg-[#0A0E17] rounded-lg p-3 space-y-2" data-testid="pause-end-template-box">
        <div className="flex items-center gap-2">
          <PauseCircle className="h-4 w-4 text-[#F59E0B]" />
          <span className="text-sm text-slate-300">Template di bentornato a fine pausa</span>
        </div>
        <Select value={pauseTplId || "none"} onValueChange={setPauseTplId}>
          <SelectTrigger className="h-9 bg-[#111827] border-[#1F2937] text-slate-100 text-sm" data-testid="pause-end-template-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
            <SelectItem value="none">Nessuno (riattivazione silenziosa)</SelectItem>
            {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.status ? ` · ${t.status}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-500">Quando la pausa scade il cliente torna iscritto e riceve questo template approvato Meta (necessario perché la finestra 24h è chiusa), così sa che riprenderà a ricevere le novità.</p>
        <Button type="button" variant="outline" onClick={createWelcomeBack} disabled={creatingTpl} data-testid="welcome-back-preset-create"
                className="bg-[#F59E0B]/10 border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/20 h-8">
          {creatingTpl ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />} Crea «Bentornato {"{{nome}}"}» e invialo a Meta per approvazione
        </Button>
        {tplResult && (
          <p className="text-[11px]" data-testid="welcome-back-preset-result">
            {tplResult.meta_error ? <span className="text-amber-400">{tplResult.meta_error}</span>
              : <span className="text-[#10B981]">Template «{tplResult.template?.name}» {tplResult.created ? "creato e " : ""}inviato a Meta ({tplResult.template?.meta_status || tplResult.template?.status}); già impostato come template di fine pausa. Lo stato di approvazione si aggiorna nella sezione Template.</span>}
          </p>
        )}
      </div>
      <div className="bg-[#0A0E17] rounded-lg p-3 space-y-2" data-testid="unsub-preset-box">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-[#8B5CF6]" />
          <span className="text-sm text-slate-300">Automazione pronta «Disiscrizione con motivo»</span>
        </div>
        <p className="text-[11px] text-slate-500">Allo STOP propone «Pausa di 30 giorni» (il cliente viene riattivato automaticamente alla scadenza) oppure la disiscrizione definitiva chiedendo il motivo (prezzo, troppi messaggi, non interessato, altro), che compare nel Report.</p>
        <Button type="button" variant="outline" onClick={createPreset} disabled={creatingPreset} data-testid="unsub-preset-create"
                className="bg-[#8B5CF6]/10 border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#8B5CF6]/20 h-8">
          {creatingPreset ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />} Crea / apri l'automazione
        </Button>
      </div>
      <Button onClick={save} disabled={saving} data-testid="stop-words-save" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva
      </Button>
    </div>
  );
}
